import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { adminApi, apiError } from "@/lib/api";
import { ADMIN } from "@/constants/testIds";
import { fmtDate, fmtNum, StatusPill, Panel, PageHeader, Th, Td, Empty } from "@/components/admin/parts";
import { ConfirmDialog } from "@/components/admin/ConfirmDialog";

const TABS = [
  { key: "ledger", label: "Historia operacji" },
  { key: "wallets", label: "Portfele użytkowników" },
  { key: "adjust", label: "Korekty ręczne" },
];
const LEDGER_TYPES = [
  { key: "all", label: "Wszystkie" },
  { key: "purchase", label: "Zakupy" },
  { key: "bonus", label: "Bonusy" },
  { key: "charge", label: "Operacje AI" },
  { key: "refund", label: "Zwroty" },
  { key: "adjustment", label: "Korekty" },
];

const SummaryCard = ({ label, value, tone }) => (
  <div className="rounded-panel border border-brand-line bg-brand-surface p-5">
    <p className={`font-display text-2xl font-bold ${tone || "text-brand-text"}`}>{fmtNum(value)}</p>
    <p className="mt-0.5 text-xs text-brand-muted">{label}</p>
  </div>
);

export default function Credits() {
  const [tab, setTab] = useState("ledger");
  const [summary, setSummary] = useState(null);
  const [ledger, setLedger] = useState(null);
  const [wallets, setWallets] = useState(null);
  const [type, setType] = useState("all");
  const [q, setQ] = useState("");
  // adjustment form
  const [email, setEmail] = useState("");
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [confirm, setConfirm] = useState(false);
  const [loading, setLoading] = useState(false);

  const loadLedger = useCallback(() => {
    adminApi.creditsLedger({ type, q: q || undefined, limit: 100 }).then(setLedger).catch(() => {});
  }, [type, q]);

  useEffect(() => { adminApi.creditsSummary().then(setSummary).catch(() => {}); }, []);
  useEffect(() => { if (tab === "ledger") loadLedger(); if (tab === "wallets") adminApi.creditsWallets({ limit: 100 }).then(setWallets).catch(() => {}); }, [tab, loadLedger]);

  const doAdjust = async () => {
    setLoading(true);
    try {
      const users = await adminApi.users({ q: email.trim(), limit: 1 });
      const u = users.items?.[0];
      if (!u || u.email.toLowerCase() !== email.trim().toLowerCase()) { toast.error("Nie znaleziono konta o tym adresie."); setLoading(false); return; }
      const res = await adminApi.adjustCredits(u.id, { amount: parseInt(amount, 10), reason: reason.trim() });
      toast.success(`Korekta zapisana. Nowe saldo: ${res.balance}`);
      setConfirm(false); setEmail(""); setAmount(""); setReason("");
      adminApi.creditsSummary().then(setSummary);
      if (tab === "ledger") loadLedger();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const openConfirm = () => {
    const amt = parseInt(amount, 10);
    if (!email.trim() || !amt || !reason.trim()) { toast.error("Uzupełnij e-mail, kwotę (≠0) i powód."); return; }
    setConfirm(true);
  };

  return (
    <div>
      <PageHeader title="Kredyty" subtitle="Rejestr operacji kredytowych (append-only)" />

      {summary && (
        <div className="mb-5 grid grid-cols-2 gap-4 lg:grid-cols-4">
          <SummaryCard label="W obiegu" value={summary.inCirculation} tone="text-brand-accent" />
          <SummaryCard label="Sprzedane" value={summary.sold} />
          <SummaryCard label="Wykorzystane" value={summary.used} />
          <SummaryCard label="Zwrócone" value={summary.returned} />
        </div>
      )}

      <div className="mb-4 flex flex-wrap gap-2">
        {TABS.map((t) => (
          <button key={t.key} data-testid={`${ADMIN.creditsTab}-${t.key}`} onClick={() => setTab(t.key)} className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${tab === t.key ? "bg-brand-accent text-brand-on" : "border border-brand-line text-brand-sec hover:text-brand-text"}`}>{t.label}</button>
        ))}
      </div>

      {tab === "ledger" && (
        <Panel testId={ADMIN.ledgerTable}>
          <div className="mb-4 flex flex-wrap items-center gap-2">
            {LEDGER_TYPES.map((t) => (
              <button key={t.key} onClick={() => setType(t.key)} className={`rounded-full px-3 py-1.5 text-xs font-medium ${type === t.key ? "bg-brand-elevated text-brand-accent" : "text-brand-sec hover:text-brand-text"}`}>{t.label}</button>
            ))}
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Szukaj (e-mail / powiązanie)" className="ml-auto h-9 w-full max-w-xs rounded-field border border-brand-line bg-brand-surface px-3 text-sm text-brand-text outline-none" />
          </div>
          {!ledger ? <Empty>Ładowanie…</Empty> : ledger.items.length === 0 ? <Empty>Brak operacji.</Empty> : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[820px]">
                <thead><tr className="border-b border-brand-line"><Th>Data</Th><Th>Użytkownik</Th><Th>Typ</Th><Th className="text-right">Zmiana</Th><Th className="text-right">Saldo po</Th><Th>Powiązanie</Th></tr></thead>
                <tbody>
                  {ledger.items.map((e) => (
                    <tr key={e.id} className="border-b border-brand-line/60">
                      <Td className="whitespace-nowrap text-brand-sec">{fmtDate(e.created_at)}</Td>
                      <Td className="text-brand-sec">{e.user_email}</Td>
                      <Td><StatusPill status={e.type_label} label={e.type_label} /></Td>
                      <Td className={`text-right font-semibold ${e.amount >= 0 ? "text-brand-accent" : "text-brand-sec"}`}>{e.amount > 0 ? "+" : ""}{e.amount}</Td>
                      <Td className="text-right text-brand-text">{e.balance_after}</Td>
                      <Td className="max-w-[180px] truncate text-xs text-brand-muted">{e.reference || "—"}</Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
      )}

      {tab === "wallets" && (
        <Panel testId={ADMIN.walletsTable}>
          {!wallets ? <Empty>Ładowanie…</Empty> : wallets.items.length === 0 ? <Empty>Brak portfeli.</Empty> : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[640px]">
                <thead><tr className="border-b border-brand-line"><Th>Użytkownik</Th><Th className="text-right">Saldo</Th><Th className="text-right">Kupione</Th><Th className="text-right">Bonus</Th><Th className="text-right">Wydane</Th></tr></thead>
                <tbody>
                  {wallets.items.map((w) => (
                    <tr key={w.user_id} className="border-b border-brand-line/60">
                      <Td className="text-brand-sec">{w.user_email}</Td>
                      <Td className="text-right font-semibold text-brand-accent">{fmtNum(w.balance)}</Td>
                      <Td className="text-right">{fmtNum(w.total_purchased)}</Td>
                      <Td className="text-right">{fmtNum(w.total_bonus)}</Td>
                      <Td className="text-right">{fmtNum(w.total_spent)}</Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
      )}

      {tab === "adjust" && (
        <Panel title="Dodaj korektę salda">
          <div className="max-w-lg space-y-3">
            <input data-testid={ADMIN.ledgerAdjustEmail} value={email} onChange={(e) => setEmail(e.target.value)} placeholder="E-mail użytkownika" className="h-10 w-full rounded-field border border-brand-line bg-brand-surface px-3 text-sm text-brand-text outline-none" />
            <input data-testid={ADMIN.ledgerAdjustAmount} type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Kwota (np. +10 lub -5)" className="h-10 w-full rounded-field border border-brand-line bg-brand-surface px-3 text-sm text-brand-text outline-none" />
            <textarea data-testid={ADMIN.ledgerAdjustReason} value={reason} onChange={(e) => setReason(e.target.value)} rows={2} placeholder="Powód korekty (wymagany, trafi do audytu)" className="w-full rounded-field border border-brand-line bg-brand-surface px-3 py-2 text-sm text-brand-text outline-none" />
            <button data-testid={ADMIN.ledgerAdjustSubmit} onClick={openConfirm} className="h-10 rounded-field bg-brand-accent px-5 text-sm font-semibold text-brand-on">Zastosuj korektę</button>
            <p className="text-xs text-brand-muted">Korekta tworzy dokładnie jeden wpis w rejestrze i wpis w audycie. Nie zmienia historii opłaconych transakcji.</p>
          </div>
        </Panel>
      )}

      <ConfirmDialog
        open={confirm}
        onOpenChange={setConfirm}
        title="Potwierdź korektę salda"
        description={`Zmienisz saldo konta ${email} o ${amount} kredytów.`}
        details={<>Powód: <span className="text-brand-sec">{reason}</span></>}
        confirmLabel="Zapisz korektę"
        onConfirm={doAdjust}
        loading={loading}
        testId="admin-credit-confirm"
      />
    </div>
  );
}
